<template>
  <gsp-card class="gsp-card-elevation1 has-tooltip">
    <div class="heading-buttons vm-mb-7">
      <h2 v-t="'labels.bankHeading'" />
      <gsp-secondary-button
        v-if="isAddBankButtonEnabled"
        ref="addBankButton"
        @click="isAddBankModalVisible = true"
        class="gsp-secondary-button-default"
        :aria-label="t('labels.addBank')"
      >
        <gsp-icon slot="start">building-columns</gsp-icon>
        <span class="vm-align-middle" v-t="'labels.addBank'" />
      </gsp-secondary-button>
    </div>
    <AllServiceDataConfirmation
      v-if="bankSubmittedData"
      :service-data="bankSubmittedData.serviceConfirmationData"
    >
      <template #success>
        <gsp-alert-success
          v-if="bankSubmittedData.type == 'add'"
          v-html-t="'htmlMarkup.addBankSuccessMessage'"
          role="alert"
          tabindex="-1"
        />
        <gsp-alert-accent
          v-else
          v-html-t="[
            'htmlMarkup.deleteBankSuccessMessage',
            bankSubmittedData.bankData,
          ]"
          tabindex="-1"
          role="alert"
        />
      </template>
      <template
        v-if="bankSubmittedData.type == 'add'"
        #error="{ curServiceData, isLastService }"
      >
        <AddBankErrorMessage
          :error-data="bankSubmittedData.error"
          :bank-data="bankSubmittedData.bankData"
        >
          <template #no-error-found>
            <ServiceErrorAlert
              :cur-service-data="curServiceData"
              :is-last-service="isLastService"
            />
          </template>
        </AddBankErrorMessage>
      </template>
    </AllServiceDataConfirmation>
    <AllServiceDataConfirmation :service-data="serviceConfirmationData" />
    <gsp-card-body>
      <VLoader v-if="isLoading" />
      <div v-else>
        <gsp-alert-accent
          v-if="!isAddBankButtonEnabled"
          v-html-t="'htmlMarkup.doNotHaveAccessToAddBankMessage'"
          class="vm-mb-4"
          tabindex="-1"
        />
        <div
          class="vm-grid sm:vm-grid-cols-1 md:vm-grid-cols-2 lg:vm-grid-cols-3 xl:vm-grid-cols-3 vm-gap-8"
        >
          <gsp-card
            class="gsp-card-elevation1 vm-font-2"
            v-for="(bankData, bankIndex) in bankList"
            :key="bankIndex"
          >
            <gsp-card-body>
              <BankShortDetail
                :data="{
                  bankData,
                  customerProfileName,
                }"
              />
              <gsp-primary-anchor
                :id="getBankElementId(bankIndex, 'associated-account')"
                v-t="'labels.bankViewAssociatedAccount'"
                href="javascript:void(0)"
                :aria-label="
                  t('labels.bankViewAssociatedAccountAriaLabel', {
                    ...getDataForLabel(bankData),
                    registrationLines: getRegistrationLines(bankData),
                  })
                "
              />
              <VTooltip
                class="tooltip-w-auto"
                :anchor="getBankElementId(bankIndex, 'associated-account')"
                auto-update-mode="auto"
                position="bottom"
              >
                <p
                  class="vm-mb-3 vm-pls-typo-dt-gsp-design-h5 !vm-font-semibold"
                  v-t="'labels.associateAccount'"
                />
                <p v-html="getRegistrationLines(bankData)" />
              </VTooltip>
            </gsp-card-body>
            <gsp-card-actions class="vm-mt-7 vm-flex vm-items-center">
              <p v-if="bankData.hasSystematic">
                <VTRuntimeTemplate
                  :templateT="'htmlMarkup.deleteBankSystematicAgreement'"
                  :template-props="getDataForLabel(bankData)"
                />
              </p>
              <gsp-tertiary-button
                v-else-if="bankData.allowDelete"
                :ref="(el) => setDeleteButtonRef(el, bankData)"
                @click="showDeleteBankModal(bankData)"
                class="gsp-tertiary-button-default"
                :aria-label="
                  t('labels.deleteBankAriaLabel', getDataForLabel(bankData))
                "
              >
                <gsp-icon slot="start">trash-can</gsp-icon>
                <span class="vm-align-middle" v-t="'labels.delete'" />
              </gsp-tertiary-button>
              <template v-else>
                <p
                  v-html-t="[
                    'htmlMarkup.contactToDeleteBank',
                    {
                      tooltipAnchorId: getBankElementId(bankIndex, 'contact'),
                      ...getDataForLabel(bankData),
                    },
                  ]"
                  :id="getBankElementId(bankIndex, 'contact')"
                />
                <VTooltip
                  :anchor="getBankElementId(bankIndex, 'contact')"
                  auto-update-mode="auto"
                  position="bottom"
                >
                  <p v-html-t="'htmlMarkup.mainContactUsStatement'" />
                </VTooltip>
              </template>
            </gsp-card-actions>
          </gsp-card>
        </div>
      </div>
    </gsp-card-body>
  </gsp-card>
  <AddBankModal
    v-if="isAddBankModalVisible"
    opener="addBankButtonRef"
    :customerProfileName="customerProfileName"
    @close-modal="isAddBankModalVisible = false"
    @submitted="bankSubmitted('add', $event)"
  />
  <DeleteBankModal
    v-if="isDeleteModalVisible"
    :model-value="deleteBankData"
    opener="activeDeleteButtonRef"
    @close-modal="isDeleteModalVisible = false"
    @submitted="bankSubmitted('delete', $event)"
  />
</template>

<script>
let instanceCount = 0;
</script>

<script setup>
import { ref, provide } from "vue";
import AddBankModal from "@/components/modals/AddBankModal";
import DeleteBankModal from "@/components/modals/DeleteBankModal";
import { provideHttpUIEffect } from "~/lib/httpUIEffect/index";
import { generateActiveElDynamicRef } from "~/composables/ref-generator";
import { convertLinesArrToHtml } from "~/utils/miscellaneous";
import BankShortDetail from "@/components/BankShortDetail";
import AddBankErrorMessage from "@/components/AddBankErrorMessage";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const instanceId = `bank-information-${instanceCount++}`;

const {
  dataloader,
  isLoading,
  serviceConfirmationData,
} = provideHttpUIEffect();

const bankList = ref(null);
const customerProfileName = ref(null);

const isAddBankModalVisible = ref(false);
const isDeleteModalVisible = ref(false);
const deleteBankData = ref(null);
const bankSubmittedData = ref(null);

const addBankButton = ref(null);
const isAddBankButtonEnabled = ref(false);

const {
  allElRef: allDeleteButtonRef,
  setElRefFn: setDeleteButtonRef,
  activeElRef: activeDeleteButtonRef,
} = generateActiveElDynamicRef({
  refIdGetter: ({ id }) => id,
  activeElRefIdGetterParam: () => deleteBankData.value.bankData,
});

provide("addBankButtonRef", addBankButton);
provide("activeDeleteButtonRef", activeDeleteButtonRef);

function getBankElementId(bankIndex, type) {
  return `${instanceId}-${type}-${bankIndex}`;
}

function showDeleteBankModal(bankData) {
  isDeleteModalVisible.value = true;
  deleteBankData.value = {
    bankData,
    customerProfileName,
  };
}

async function loadBankData() {
  allDeleteButtonRef.value = {};
  isAddBankButtonEnabled.value = false;
  bankList.value = [];
  customerProfileName.value = null;

  const bankUiData = await dataloader.getBankList();

  customerProfileName.value = bankUiData.customerProfile.name;
  isAddBankButtonEnabled.value = bankUiData.allowAddBank;
  bankList.value = bankUiData.bankList;
}

function bankSubmitted(type, { serviceConfirmationData, bankData, error }) {
  bankSubmittedData.value = {
    type,
    serviceConfirmationData,
    bankData,
    error,
  };
  loadBankData();
}

function getRegistrationLines({
  registrationLine1,
  registrationLine2,
  registrationLine3,
}) {
  return convertLinesArrToHtml([
    registrationLine1,
    registrationLine2,
    registrationLine3,
  ]);
}

function getDataForLabel(bankData) {
  return {
    ...bankData,
    customerProfileName: customerProfileName.value,
  };
}

loadBankData();
</script>
